import type { Dispatch, MiddlewareAPI, UnknownAction } from '@reduxjs/toolkit'
import { IEmbedSourceContext } from '@vedalib/courseAnalitycs/lib/events/course/CourseInitialized'

import { getWindowData } from 'services/ScormData/ScormData'
import { getSection, getBlockId } from 'services/Store/Project/selectors'
import type { IProjectContext } from 'services/Store/Project/types'
import { notEmpty } from 'utils/notEmpty'

import type { LmsService } from '../lms.service'

interface IContentSpaceContext {
  isContentSpace: boolean
  portalId: string | null
}

const NOT_CONTENT_SPACE: IContentSpaceContext = { isContentSpace: false, portalId: null }

const getContentSpaceContext = (frame: Window = window): IContentSpaceContext => {
  if (frame === frame.parent) {
    // Portal V2 inline player: no iframe, course runs in the portal page itself.
    // Detect by checking the current page URL directly.
    const pathname = frame.location.pathname
    const portalMatch = pathname.match(/\/contentspace\/public\/portal\/([a-f0-9-]{36})/)
    if (portalMatch) {
      return { isContentSpace: true, portalId: portalMatch[1] }
    }
    if (pathname.match(/\/courses\/[a-f0-9-]{36}/)) {
      return { isContentSpace: true, portalId: null } // custom domain
    }
    return NOT_CONTENT_SPACE
  }
  try {
    // v1 vesion sync with back and remove
    const hash = frame.parent.location.hash
    const pathname = frame.parent.location.pathname

    if (hash.match(/\/contentspace\/player\/[a-f0-9-]{36}/)) {
      return { isContentSpace: true, portalId: null } //internal player
    }

    const portalMatch = pathname.match(/\/contentspace\/public\/portal\/([a-f0-9-]{36})/)
    if (portalMatch) {
      return { isContentSpace: true, portalId: portalMatch[1] } // public portal
    }

    if (pathname.match(/\/courses\/[a-f0-9-]{36}/)) {
      return { isContentSpace: true, portalId: null } // custom domain: /courses/{id}
    }

    return getContentSpaceContext(frame.parent)
  } catch (err) {
    console.warn('resolveSourceContext cross-origin', err)
    return NOT_CONTENT_SPACE
  }
}

export interface ICommonVariables {
  companyId: string
  projectId: string
  courseId: string
  sectionId: string
  blockId: string
}

export interface IHandlerContext {
  state: IProjectContext
  lms: LmsService
  dispatch: Dispatch
  scormSettings: NonNullable<
    ReturnType<typeof import('services/Store/Project/selectors').getScormSettings>
  >
  commonVars: ICommonVariables
  store: MiddlewareAPI<Dispatch<UnknownAction>, { project: IProjectContext }>
}

export const getCommonVariables = (state: IProjectContext): ICommonVariables => {
  const { id: projectId, companyId } = state.data.project
  return {
    companyId: companyId || '',
    projectId,
    courseId: projectId,
    sectionId: getSection(state)?.id || '',
    blockId: getBlockId(state) || '',
  }
}

export const getOsInfo = (): string => {
  const ua = navigator.userAgent
  const platform = navigator.platform

  if (/iPhone|iPad|iPod/.test(ua)) {
    return 'iOS'
  }
  if (/Android/.test(ua)) {
    return 'Android'
  }
  if (/Mac/.test(platform)) {
    return 'macOS'
  }
  if (/Win/.test(platform)) {
    return 'Windows'
  }
  if (/Linux/.test(platform) && !/Android/.test(ua)) {
    return 'Linux'
  }
  if (/CrOS/.test(ua)) {
    return 'Chrome OS'
  }
  return 'unknown'
}

export const getDeviceType = (): string => {
  return /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop'
}

export const getEmbedHostDomain = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const placementUrl = urlParams.get('placement')
  const isEmbed =
    urlParams.get('embed') === 'true' ||
    urlParams.get('enabledSections') ||
    urlParams.get('enabledBlocks')

  if (!isEmbed) {
    return null
  }
  try {
    if (placementUrl) {
      return new URL(placementUrl).hostname
    }
  } catch {
    return placementUrl
  }
  try {
    const ancestorOrigin = location.ancestorOrigins?.[0]
    if (ancestorOrigin) {
      return new URL(ancestorOrigin).hostname
    }
  } catch {
    console.error('Error parsing ancestor origin')
  }
  try {
    if (document.referrer) {
      return new URL(document.referrer).hostname
    }
  } catch {
    return document.referrer
  }
  return null
}

export const resolveSourceContext = (exportId?: string): IEmbedSourceContext => {
  const params = new URLSearchParams(window.location.search)
  const isEmbed = params.get('embed') === 'true'

  if (isEmbed) {
    const enabledSections = params.get('enabledSections')
    const enabledBlocks = params.get('enabledBlocks')
    const sourceType = enabledBlocks
      ? 'embed_block'
      : enabledSections
        ? 'embed_section'
        : 'embed_course'
    const embedId = [exportId, enabledSections, enabledBlocks].filter(notEmpty).join(':')
    return { sourceType, embedId }
  }
  // TODO: possibly is not necessary portalId and think about isContentSpace
  const { isContentSpace, portalId } = getContentSpaceContext()
  if (isContentSpace) {
    const windowData = getWindowData()
    const resolvedPortalId = portalId || windowData?.analyticsConfig?.override?.portalId
    const portalName = windowData?.analyticsConfig?.override?.portalName
    return { sourceType: 'contentspace_course', portalId: resolvedPortalId, portalName }
  }
  return { sourceType: 'lms_course' }
}

const FALLBACK_DOMAIN = 'unknown.local'
const INVALID_EMAIL_DOMAINS = /^(localhost|127\.\d+\.\d+\.\d+|::1)$/

export const getLaunchDomain = () => {
  const url = getLaunchUrl()
  try {
    const hostname = new URL(url).hostname
    if (!hostname || INVALID_EMAIL_DOMAINS.test(hostname)) {
      return FALLBACK_DOMAIN
    }
    return hostname
  } catch {
    return url || FALLBACK_DOMAIN
  }
}

export const getLaunchUrl = () => {
  const urlParams = new URLSearchParams(window.location.search)
  const lmsUrlOriginOfDynamic = urlParams.get('lmsOrigin')
  const url = lmsUrlOriginOfDynamic || document.referrer || window.location.href
  try {
    return getEmbedHostDomain() || new URL(url).origin
  } catch {
    return url
  }
}
